import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

const LENS_RATIO = 0.4;
const DESKTOP_MIN_WIDTH = 992;

const getImageBounds = (container, img) => {
  const { width: containerWidth, height: containerHeight } =
    container.getBoundingClientRect();
  const { naturalWidth, naturalHeight } = img;

  if (!naturalWidth || !naturalHeight) return null;

  const containerRatio = containerWidth / containerHeight;
  const imageRatio = naturalWidth / naturalHeight;

  let renderedWidth;
  let renderedHeight;
  let offsetX;
  let offsetY;

  if (imageRatio > containerRatio) {
    renderedWidth = containerWidth;
    renderedHeight = containerWidth / imageRatio;
    offsetX = 0;
    offsetY = (containerHeight - renderedHeight) / 2;
  } else {
    renderedHeight = containerHeight;
    renderedWidth = containerHeight * imageRatio;
    offsetX = (containerWidth - renderedWidth) / 2;
    offsetY = 0;
  }

  return { renderedWidth, renderedHeight, offsetX, offsetY };
};

const computeFlyoutLayout = (bounds, containerRect, flyoutAnchorRef) => {
  const pad = 10;
  const viewportMargin = 16;
  const aspect = bounds.renderedWidth / bounds.renderedHeight;
  const paneTop = containerRect.top + bounds.offsetY;
  const maxPaneH = Math.min(
    bounds.renderedHeight,
    window.innerHeight - paneTop - viewportMargin
  );

  const anchorEl = flyoutAnchorRef?.current;
  const anchorRect = anchorEl?.getBoundingClientRect();

  if (anchorRect && anchorRect.width > 80) {
    let paneW = anchorRect.width - pad * 2;
    let paneH = paneW / aspect;

    if (paneH > maxPaneH) {
      paneH = maxPaneH;
      paneW = paneH * aspect;
    }

    paneW = Math.min(paneW, bounds.renderedWidth);
    paneH = Math.min(paneH, bounds.renderedHeight);

    return {
      paneLeft: anchorRect.left + pad,
      paneTop,
      paneWidth: paneW,
      paneHeight: paneH,
    };
  }

  const gap = 12;
  let paneW = bounds.renderedWidth;
  let paneH = bounds.renderedHeight;
  let paneLeft = containerRect.left + bounds.offsetX + bounds.renderedWidth + gap;

  const spaceRight = window.innerWidth - paneLeft - viewportMargin;
  if (spaceRight < paneW) {
    paneW = Math.max(spaceRight, 200);
    paneH = paneW / aspect;
  }

  if (paneH > maxPaneH) {
    paneH = maxPaneH;
    paneW = paneH * aspect;
  }

  if (paneLeft + paneW > window.innerWidth - viewportMargin) {
    paneLeft = containerRect.left + bounds.offsetX - paneW - gap;
  }

  return { paneLeft, paneTop, paneWidth: paneW, paneHeight: paneH };
};

const ZoomableImage = ({ src, alt, enabled, flyoutAnchorRef, onZoom, onZoomEnd }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [lens, setLens] = useState(null);

  const updateZoom = useCallback(
    (clientX, clientY) => {
      const container = containerRef.current;
      const img = imgRef.current;
      if (!enabled || !container || !img) return;

      const bounds = getImageBounds(container, img);
      if (!bounds) return;

      const rect = container.getBoundingClientRect();
      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;

      if (
        mouseX < bounds.offsetX ||
        mouseX > bounds.offsetX + bounds.renderedWidth ||
        mouseY < bounds.offsetY ||
        mouseY > bounds.offsetY + bounds.renderedHeight
      ) {
        setLens(null);
        onZoomEnd();
        return;
      }

      const relX = mouseX - bounds.offsetX;
      const relY = mouseY - bounds.offsetY;

      const lensW = bounds.renderedWidth * LENS_RATIO;
      const lensH = bounds.renderedHeight * LENS_RATIO;

      const lensLeft = Math.max(
        0,
        Math.min(relX - lensW / 2, bounds.renderedWidth - lensW)
      );
      const lensTop = Math.max(
        0,
        Math.min(relY - lensH / 2, bounds.renderedHeight - lensH)
      );

      const { paneLeft, paneTop, paneWidth, paneHeight } = computeFlyoutLayout(
        bounds,
        rect,
        flyoutAnchorRef
      );

      const scaleX = paneWidth / lensW;
      const scaleY = paneHeight / lensH;
      const bgW = bounds.renderedWidth * scaleX;
      const bgH = bounds.renderedHeight * scaleY;
      const bgX = -lensLeft * scaleX;
      const bgY = -lensTop * scaleY;

      setLens({
        left: bounds.offsetX + lensLeft,
        top: bounds.offsetY + lensTop,
        width: lensW,
        height: lensH,
      });

      onZoom({
        src,
        paneLeft,
        paneTop,
        paneWidth,
        paneHeight,
        bgW,
        bgH,
        bgX,
        bgY,
      });
    },
    [enabled, flyoutAnchorRef, onZoom, onZoomEnd, src]
  );

  const handleMouseMove = (e) => updateZoom(e.clientX, e.clientY);

  const handleMouseLeave = () => {
    setLens(null);
    onZoomEnd();
  };

  return (
    <div
      ref={containerRef}
      className={`amazon-zoom-container${enabled ? " amazon-zoom-enabled" : ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="amazon-zoom-img border-lg"
        draggable={false}
        onError={(e) => {
          e.target.src = "https://api.welbeing.in/uploads/products/no_img.jpg";
        }}
      />
      {enabled && lens && (
        <div
          className="amazon-zoom-lens border-lg"
          style={{
            left: lens.left,
            top: lens.top,
            width: lens.width,
            height: lens.height,
          }}
        />
      )}
    </div>
  );
};

const ProductImageZoom = ({ images, activeIndex = 0, flyoutAnchorRef }) => {
  const [zoomState, setZoomState] = useState(null);
  const [canZoom, setCanZoom] = useState(false);

  useEffect(() => {
    const checkViewport = () => setCanZoom(window.innerWidth >= DESKTOP_MIN_WIDTH);
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  useEffect(() => {
    setZoomState(null);
  }, [activeIndex]);

  const handleZoomEnd = useCallback(() => setZoomState(null), []);

  const activeImage = images[activeIndex] || images[0];

  const zoomFlyout =
    canZoom &&
    zoomState &&
    createPortal(
      <div
        className="amazon-zoom-flyout border-lg"
        style={{
          left: zoomState.paneLeft,
          top: zoomState.paneTop,
          width: zoomState.paneWidth,
          height: zoomState.paneHeight,
        }}
      >
        <img
          src={zoomState.src}
          alt=""
          className="amazon-zoom-flyout-img border-lg"
          style={{
            width: zoomState.bgW,
            height: zoomState.bgH,
            left: zoomState.bgX,
            top: zoomState.bgY,
          }}
          onError={(e) => {
            e.target.src = "https://api.welbeing.in/uploads/products/no_img.jpg";
          }}
          draggable={false}
        />
      </div>,
      document.body
    );

  if (!activeImage) return null;

  return (
    <div className="amazon-zoom-wrap mainSlider2 border-lg">
      <ZoomableImage
        key={activeIndex}
        src={activeImage}
        alt={`Product ${activeIndex + 1}`}
        enabled={canZoom}
        flyoutAnchorRef={flyoutAnchorRef}
        onZoom={setZoomState}
        onZoomEnd={handleZoomEnd}
      />
      {zoomFlyout}
    </div>
  );
};

export default ProductImageZoom;
